import { Children, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { LeftNavButton, RightNavButton } from './SliderComponents';

interface Props {
    itemHeight: number;
    itemWidth: number;
    initialItemsToShow: number;
    initialOffset: number;
    className: string;
    children: ReactNode;
}
export const LazyLoadSlider = (props: Props) => {
    const itemsToSlide = 4;
    const sliderRef = useRef<HTMLDivElement>(null);
    const sliderTrackRef = useRef<HTMLDivElement>(null);
    const [offsetValue, setOffsetValue] = useState(props.initialOffset);
    const [itemsToShowState, setItemsToShowState] = useState(props.initialItemsToShow);
    const [slideDirection, setSlideDirection] = useState(0);

    const updateState = useCallback(() => {
        const parentWidth = sliderRef?.current?.offsetWidth ?? 2000;
        let itemsToShow = Children.count(props.children);
        if (sliderRef.current) {
            itemsToShow = Math.min(itemsToShow, Math.floor(parentWidth / props.itemWidth));
        }
        setItemsToShowState(itemsToShow);
    }, [props.children, props.itemWidth]);

    useEffect(() => {
        updateState();
        window.addEventListener('resize', updateState);
        return () => {
            window.removeEventListener('resize', updateState);
        };
    }, [updateState]);

    useEffect(() => {
        if (sliderTrackRef.current) {
            sliderTrackRef.current.style.left = `${-props.itemWidth * offsetValue + 15}px`;
        }
        slideDirection !== 0 && setTimeout(() => setSlideDirection(0), 500);
    }, [offsetValue, props.itemWidth, slideDirection]);

    function slideOffset(offset) {
        let nextOffset = offsetValue + offset;
        if (nextOffset < 0) {
            nextOffset = 0;
        }
        if (nextOffset + itemsToShow >= Children.count(props.children)) {
            nextOffset = Children.count(props.children) - itemsToShowState + 1;
        }
        const slideDirection = offset > 0 ? 1 : -1;
        setOffsetValue(nextOffset);
        setSlideDirection(slideDirection);
    }

    let disabled = true;
    const itemsToShow = itemsToShowState;

    let children: JSX.Element[] | null | undefined = [];
    if (props.children) {
        const offset = offsetValue;
        let start = offset - 1,
            end = offset + itemsToShow + 1;
        if (slideDirection !== 0) {
            start -= itemsToSlide;
            end += itemsToSlide;
        }
        start = Math.max(start, 0);
        end = Math.min(end, Children.count(props.children));
        children = Children.map(props.children, (child, index) => {
            if (index >= start && index <= end) {
                return (
                    <div key={index} style={{ width: `${props.itemWidth}px` }}>
                        {child}
                    </div>
                );
            }
            return (
                <div
                    key={index}
                    style={{
                        width: `${props.itemWidth}px`,
                        height: '1px'
                    }}
                >
                    {null}
                </div>
            );
        });

        disabled =
            offset + itemsToShow >= Children.count(props.children) || itemsToShow === Children.count(props.children);
    }

    let className = 'slider';
    if (props.className) {
        className += ` ${props.className}`;
    }

    return (
        <div className={className} ref={sliderRef} data-displayname="TileSlider">
            <LeftNavButton
                onClick={() => slideOffset(-itemsToSlide)}
                disabled={offsetValue === 0}
                itemHeight={props.itemHeight}
            />
            <div
                className="slider-track native-slide"
                ref={sliderTrackRef}
                style={{
                    width: `${props.itemWidth * Children.count(props.children)}px`
                }}
            >
                {children}
            </div>
            <RightNavButton
                onClick={() => slideOffset(itemsToSlide)}
                disabled={disabled}
                itemHeight={props.itemHeight}
            />
        </div>
    );
};
